import { File, Download } from 'lucide-react';
import type { ModelFile } from '@maker-toolchest/shared';
import { Button } from '@/components/Button';
import { useFilePlates } from '../api';

interface FilesTableProps {
  modelId: string;
  files: ModelFile[];
  onDownload: (fileId: string, fileName: string) => void;
  onFileClick?: (fileId: string) => void;
  maxRows?: number;
}

function FileRow({
  modelId,
  file,
  onDownload,
  onFileClick
}: {
  modelId: string;
  file: ModelFile;
  onDownload: (fileId: string, fileName: string) => void;
  onFileClick?: (fileId: string) => void;
}) {
  const { data: platesResponse } = useFilePlates(
    modelId,
    file.id,
    file.metadataStatus === 'COMPLETED'
  );

  const plates = platesResponse?.plates || [];
  const plateCount = plates.length;
  const objectCount = plates.reduce((sum, plate) => sum + (plate.objects?.length || 0), 0);

  return (
    <tr className="border-b border-border last:border-0">
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <File className="h-4 w-4 text-primary flex-shrink-0" />
          {onFileClick ? (
            <button
              onClick={() => onFileClick(file.id)}
              className="text-sm text-foreground hover:text-primary truncate transition-colors text-left"
            >
              {file.fileName}
            </button>
          ) : (
            <span className="text-sm text-foreground truncate">{file.fileName}</span>
          )}
        </div>
      </td>
      <td className="px-4 py-3 text-sm text-foreground-muted whitespace-nowrap">
        {file.metadataStatus === 'COMPLETED' ? (plateCount || 'N/A') : '-'}
      </td>
      <td className="px-4 py-3 text-sm text-foreground-muted whitespace-nowrap">
        {file.metadataStatus === 'COMPLETED' ? (objectCount || 'N/A') : '-'}
      </td>
      <td className="px-4 py-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDownload(file.id, file.fileName)}
          title="Download file"
        >
          <Download className="h-4 w-4" />
        </Button>
      </td>
    </tr>
  );
}

export function FilesTable({ modelId, files, onDownload, onFileClick, maxRows }: FilesTableProps) {
  const displayFiles = maxRows ? files.slice(0, maxRows) : files;

  if (files.length === 0) {
    return (
      <div className="text-center py-8 text-foreground-muted">
        <File className="h-12 w-12 mx-auto mb-3 opacity-50" />
        <p>No files attached to this model</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-background-subtle">
          <tr className="border-b border-border text-left">
            <th className="px-4 py-3 text-xs font-medium text-foreground-muted">File Name</th>
            <th className="px-4 py-3 text-xs font-medium text-foreground-muted">Plates</th>
            <th className="px-4 py-3 text-xs font-medium text-foreground-muted">Objects</th>
            <th className="px-4 py-3 text-xs font-medium text-foreground-muted">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-background">
          {displayFiles.map((file) => (
            <FileRow
              key={file.id}
              modelId={modelId}
              file={file}
              onDownload={onDownload}
              onFileClick={onFileClick}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}
